import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {createNotification} from "app/actions/notifications";
import {get as getRecord, update as updateRecord} from "app/requests/admin/users";
import File from "components/helpers/form/file";
import FormWithFiles from "components/helpers/form/with-files";
import {Loading} from "components/pages";
import {valueFrom, valueFromTarget} from "lib/form";
import useAppContext from "lib/hooks/use-app-context";
import usePrompt from "lib/hooks/use-prompt";
import {withoutBlankValues} from "lib/object";
import Orders from "./orders";

export default function AdminUsersEdit() {
  const {dispatch, user} = useAppContext();
  const navigate = useNavigate();
  const {id} = useParams();
  const [block, setBlock] = useState(false);
  const [changes, setChanges] = useState({});
  const [record, setRecord] = useState(null);
  const onChange = ({target}) => {
    setBlock(true);
    setChanges({...changes, [target.name]: valueFromTarget(target)});
  };
  const onSubmit = (files) => {
    const updates = withoutBlankValues({...changes, ...files});

    if(Object.keys(updates).length === 0) {
      return dispatch(createNotification({
        content: "Please make changes before submitting",
        type: "danger"
      }));
    }

    setBlock(false);
    updateRecord(record.id, {user: updates}).then(({message, success}) => {
      if(success) {
        navigate("/admin/users", {state: {message, type: "success"}});
      } else {
        dispatch(createNotification({content: message, type: "error"}));
      }
    });
  };
  const value = (name, defaultValue) => valueFrom({defaultValue, name, objects: [changes, record]});

  usePrompt({when: block});
  useEffect(() => {
    getRecord(id).then((data) => setRecord(data.user));
  }, []);

  if(!record) { return <Loading />; }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-4 offset-md-2">
          <h1 className="text-center">Edit User</h1>
          <FormWithFiles onSubmit={onSubmit}>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <label className="input-group-text" htmlFor="user-first-name">First Name</label>
              </div>
              <input
                className="form-control"
                id="user-first-name"
                name="first_name"
                onChange={onChange}
                placeholder="Mindy"
                required={true}
                type="text"
                value={value("first_name")}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <label className="input-group-text" htmlFor="user-last-name">Last Name</label>
              </div>
              <input
                className="form-control"
                id="user-last-name"
                name="last_name"
                onChange={onChange}
                placeholder="Kaling"
                required={true}
                type="text"
                value={value("last_name")}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <label className="input-group-text" htmlFor="user-email">Email</label>
              </div>
              <input
                className="form-control"
                id="user-email"
                name="email"
                onChange={onChange}
                placeholder="mindy@kaling.com"
                required={true}
                type="email"
                value={value("email")}
              />
            </div>
            <div className="input-group mb-3">
              <div className="input-group-prepend">
                <label className="input-group-text" htmlFor="user-image">Profile Image</label>
              </div>
              <File id="user-image" name="image" onChange={onChange} />
              <img alt="Preview" className="img-fluid" src={value("image")} />
            </div>
            {user && user.manage_all && [
              {field: "admin", label: "Admin"},
              {field: "manage_all", label: "Manage All (Super Admin)"},
              {field: "manage_cases", label: "Manage Cases"},
              {field: "manage_content", label: "Manage Content"},
              {field: "manage_podcast", label: "Manage Podcast"},
              {field: "manage_store", label: "Manage Store"}
            ].map(({field, label}) => (
              <div key={field} className="custom-control custom-switch text-center mb-3">
                <input
                  checked={value(field, false)}
                  className="custom-control-input"
                  id={`user-${field}`}
                  name={field}
                  onChange={onChange}
                  type="checkbox"
                />
                <label className="custom-control-label" htmlFor={`user-${field}`}>{label}</label>
              </div>
            ))}
            <div className="custom-control custom-switch text-center mb-3">
              <input
                checked={value("case_notifications", false)}
                className="custom-control-input"
                id="user-case-notifications"
                name="case_notifications"
                onChange={onChange}
                type="checkbox"
              />
              <label className="custom-control-label" htmlFor="user-case-notifications">Case Notifications</label>
            </div>
            <div className="custom-control custom-switch text-center mb-3">
              <input
                checked={value("newsletter_notifications", false)}
                className="custom-control-input"
                id="user-newsletter-notifications"
                name="newsletter_notifications"
                onChange={onChange}
                type="checkbox"
              />
              <label className="custom-control-label" htmlFor="user-newsletter-notifications">Newsletter Notifications</label>
            </div>
            <div className="custom-control custom-switch text-center mb-3">
              <input
                checked={value("post_notifications", false)}
                className="custom-control-input"
                id="user-post-notifications"
                name="post_notifications"
                onChange={onChange}
                type="checkbox"
              />
              <label className="custom-control-label" htmlFor="user-post-notifications">Post Notifications</label>
            </div>
            <div className="form-group text-center">
              <button type="submit" className="btn btn-primary">Submit</button>
            </div>
          </FormWithFiles>
        </div>
        <div className="col-md-4">
          <Orders records={record.orders} />
        </div>
      </div>
    </div>
  );
}
